import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Row, Col, Select, DatePicker, Button,
  message, Drawer, Descriptions, Space, Statistic, Input
} from 'antd';
import {
  UserOutlined, ReloadOutlined, EyeOutlined, SearchOutlined,
  LoginOutlined, MailOutlined, TeamOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import superAdminApi, { UserActivity, PaginationParams } from '../../api/superAdminApi';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import MobileDataList from '../../../../components/mobile/MobileDataList';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const getActionColor = (action: string): string => {
  if (action === 'LOGIN') return 'blue';
  if (action === 'LOGOUT') return 'default';
  if (action.includes('CREATE')) return 'green';
  if (action.includes('UPDATE')) return 'geekblue';
  if (action.includes('DELETE')) return 'red';
  if (action.includes('CONVERT')) return 'purple';
  if (action.includes('SEND') || action.includes('LAUNCH')) return 'cyan';
  if (action.includes('COMPLETE')) return 'green';
  if (action.includes('SCORE')) return 'orange';
  if (action.includes('AI')) return 'magenta';
  return 'default';
};

const getActionIcon = (action: string) => {
  if (action === 'LOGIN') return <LoginOutlined />;
  if (action.includes('EMAIL') || action.includes('CAMPAIGN')) return <MailOutlined />;
  if (action.includes('LEAD') || action.includes('CLIENT')) return <TeamOutlined />;
  if (action.includes('COMPLETE')) return <CheckCircleOutlined />;
  return <UserOutlined />;
};

const getTargetTypeColor = (type?: string): string => {
  switch (type) {
    case 'LEAD': return 'blue';
    case 'CLIENT': return 'green';
    case 'EMAIL': return 'cyan';
    case 'CAMPAIGN': return 'purple';
    case 'TASK': return 'orange';
    case 'ORDER': return 'gold';
    case 'ACTIVITY': return 'geekblue';
    default: return 'default';
  }
};

const UserActivities: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filterAction, setFilterAction] = useState<string | undefined>();
  const [filterTargetType, setFilterTargetType] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<UserActivity | null>(null);

  const fetchActivities = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getUserActivities({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        action: filterAction,
        targetType: filterTargetType,
        search: searchText || undefined,
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setActivities(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch user activities');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterTargetType, searchText, dateRange, pagination.page, pagination.limit]);

  const fetchActionTypes = async () => {
    try {
      const response = await superAdminApi.getUserActivityActionTypes();
      if (response.success) {
        setActionTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch action types', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await superAdminApi.getUserActivityStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchActionTypes();
    fetchStats();
  }, []);

  const handleTableChange = (paginationConfig: any) => {
    fetchActivities({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  const handleViewDetails = (record: UserActivity) => {
    setSelectedActivity(record);
    setDrawerVisible(true);
  };

  const columns: ColumnsType<UserActivity> = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.userName || 'Unknown'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.userEmail}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.companyName}</Text>
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (action) => (
        <Tag color={getActionColor(action)} icon={getActionIcon(action)}>
          {action.replace(/_/g, ' ')}
        </Tag>
      )
    },
    {
      title: 'Target',
      key: 'target',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.targetType && (
            <Tag color={getTargetTypeColor(record.targetType)}>{record.targetType}</Tag>
          )}
          {record.targetName && (
            <Text style={{ fontSize: 12 }}>{record.targetName}</Text>
          )}
          {!record.targetName && record.targetId && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.targetId.substring(0, 12)}...
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (ip) => ip || '-'
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        />
      )
    }
  ];

  // Mobile card config
  const mobileCardConfig = {
    primaryField: 'action' as const,
    secondaryField: 'userEmail' as const,
    statusField: 'action' as const,
    statusConfig: Object.fromEntries(
      actionTypes.map(a => [a, { color: getActionColor(a), text: a.replace(/_/g, ' ') }])
    ),
    avatar: { icon: <UserOutlined /> },
    fields: [
      {
        key: 'userName' as const,
        label: 'User',
        render: (v: string) => <Text strong>{v}</Text>,
      },
      {
        key: 'action' as const,
        label: 'Action',
        render: (v: string) => <Tag color={getActionColor(v)}>{v.replace(/_/g, ' ')}</Tag>,
      },
      {
        key: 'targetName' as const,
        label: 'Target',
        render: (v: string) => v || '-',
      },
      {
        key: 'createdAt' as const,
        label: 'Time',
        render: (v: string) => new Date(v).toLocaleString(),
      },
    ],
    onClick: handleViewDetails,
  };

  const mobileFilters = [
    {
      key: 'action',
      placeholder: 'Action Type',
      options: actionTypes.map(a => ({ label: a.replace(/_/g, ' '), value: a })),
      value: filterAction,
      onChange: (v: string) => {
        setFilterAction(v);
        fetchActivities({ page: 1 });
      },
    },
    {
      key: 'targetType',
      placeholder: 'Target Type',
      options: [
        { label: 'Lead', value: 'LEAD' },
        { label: 'Client', value: 'CLIENT' },
        { label: 'Email', value: 'EMAIL' },
        { label: 'Campaign', value: 'CAMPAIGN' },
        { label: 'Task', value: 'TASK' },
        { label: 'Order', value: 'ORDER' },
      ],
      value: filterTargetType,
      onChange: (v: string) => {
        setFilterTargetType(v);
        fetchActivities({ page: 1 });
      },
    },
  ];

  const detailsDrawer = (
    <Drawer
      title="Activity Details"
      placement={isMobile ? 'bottom' : 'right'}
      height={isMobile ? '90%' : undefined}
      width={isMobile ? undefined : 550}
      open={drawerVisible}
      onClose={() => setDrawerVisible(false)}
    >
      {selectedActivity && (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Time">
            {new Date(selectedActivity.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="User">
            {selectedActivity.userName || selectedActivity.userEmail}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {selectedActivity.userEmail}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            <Tag>{selectedActivity.userRole}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Company">
            {selectedActivity.companyName}
          </Descriptions.Item>
          <Descriptions.Item label="Action">
            <Tag color={getActionColor(selectedActivity.action)} icon={getActionIcon(selectedActivity.action)}>
              {selectedActivity.action.replace(/_/g, ' ')}
            </Tag>
          </Descriptions.Item>
          {selectedActivity.targetType && (
            <Descriptions.Item label="Target Type">
              <Tag color={getTargetTypeColor(selectedActivity.targetType)}>
                {selectedActivity.targetType}
              </Tag>
            </Descriptions.Item>
          )}
          {selectedActivity.targetName && (
            <Descriptions.Item label="Target Name">
              {selectedActivity.targetName}
            </Descriptions.Item>
          )}
          {selectedActivity.targetId && (
            <Descriptions.Item label="Target ID">
              <Text copyable style={{ fontSize: 12 }}>{selectedActivity.targetId}</Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="IP Address">
            {selectedActivity.ipAddress || '-'}
          </Descriptions.Item>
          {selectedActivity.details && (
            <Descriptions.Item label="Details">
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>
                {JSON.stringify(selectedActivity.details, null, 2)}
              </pre>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
    </Drawer>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <UserOutlined /> User Activities
          </Title>
        </div>

        <MobileDataList
          data={activities}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No user activities found"
          onRefresh={() => fetchActivities()}
          filters={mobileFilters}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => fetchActivities({ page, limit: pageSize }),
          }}
          swipeActions={{
            right: {
              icon: <EyeOutlined />,
              color: '#1890ff',
              label: 'View',
              onClick: handleViewDetails,
            },
          }}
        />
        {detailsDrawer}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <UserOutlined /> User Activities
          </Title>
        </Col>
      </Row>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Today" value={stats.totalToday} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="This Week" value={stats.totalThisWeek} prefix={<UserOutlined />} />
            </Card>
          </Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Total" value={stats.totalAll} prefix={<TeamOutlined />} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Row style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Space wrap>
            <Input
              placeholder="Search user name or email..."
              prefix={<SearchOutlined />}
              style={{ width: 220 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => fetchActivities({ page: 1 })}
              allowClear
            />
            <Select
              placeholder="Action Type"
              style={{ width: 180 }}
              allowClear
              value={filterAction}
              onChange={(v) => {
                setFilterAction(v);
                fetchActivities({ page: 1 });
              }}
            >
              {actionTypes.map(a => (
                <Option key={a} value={a}>{a.replace(/_/g, ' ')}</Option>
              ))}
            </Select>
            <Select
              placeholder="Target Type"
              style={{ width: 140 }}
              allowClear
              value={filterTargetType}
              onChange={(v) => {
                setFilterTargetType(v);
                fetchActivities({ page: 1 });
              }}
            >
              <Option value="LEAD">Lead</Option>
              <Option value="CLIENT">Client</Option>
              <Option value="EMAIL">Email</Option>
              <Option value="CAMPAIGN">Campaign</Option>
              <Option value="TASK">Task</Option>
              <Option value="ORDER">Order</Option>
            </Select>
            <RangePicker
              onChange={(dates) => {
                setDateRange(dates as any);
                fetchActivities({ page: 1 });
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { fetchActivities(); fetchStats(); }}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={activities}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} activities`
          }}
          onChange={handleTableChange}
          size="small"
        />
      </Card>

      {detailsDrawer}
    </div>
  );
};

export default UserActivities;
